import { exists } from '@jack-henry/frontend-utils/functions';
import type { TmFormControl } from './tm-form-control';

export const FORM_CONTROL_CLASS_NAME = 'tm-form-control';

export enum TmFormControlEvent {
    ValueChanged = 'value-changed',
    ControlConnected = 'control-connected',
}

/**
 * Represents a view model for both single and multiple selectable menu items used in TM form controls.
 */
export interface SelectItemVm<T = unknown> {
    label: string;
    value: T;
    id?: number | string;
    disabled?: boolean;
}

export type SelectItemVmMapper<T> = (value: T) => SelectItemVm<T>;

/**
 * Represents a form control with a possible default value.
 */
export interface TmFormControlDefault<T> extends TmFormControl<T> {
    /**
     * The default value to use if the input does not contain an `initial` value.
     */
    default?: T;
}

function hasDefault<T>(control: TmFormControl<T>): control is TmFormControlDefault<T> {
    return 'default' in control && exists(control.default);
}

export function getDefaultValue<T>(control: TmFormControl<T>) {
    return hasDefault(control) ? control.default : undefined;
}

interface ValueChangedPayload<T> {
    value?: T;
    isValid: boolean;
}

interface ControlConnectedPayload<T = unknown> {
    control: TmFormControl<T>;
}

export type FormControlEventPayload<T = unknown> = {
    [TmFormControlEvent.ValueChanged]: ValueChangedPayload<T>;
    [TmFormControlEvent.ControlConnected]: ControlConnectedPayload<T>;
};

export type TmValueChanged<T = unknown> = CustomEvent<ValueChangedPayload<T>>;
export type TmFormControlConnected<T = unknown> = CustomEvent<ControlConnectedPayload<T>>;

export function dispatchFormControlEvent<T, Event extends TmFormControlEvent>(
    control: TmFormControl<T>,
    event: Event,
    payload: FormControlEventPayload<T>[Event]
): boolean {
    // form control dis/connect events need to bubble up to the parent form
    const exposeExternal = event === TmFormControlEvent.ControlConnected;

    const valueChangedEvent = new CustomEvent(event, {
        detail: payload,
        composed: exposeExternal,
        bubbles: exposeExternal,
    });

    return control.dispatchEvent(valueChangedEvent);
}

/**
 * Represents the result of validating a single control within a form.
 *
 * Contains any error messages associated with a failed validation.
 */
export class FormControlValidationResult<T> {
    constructor(
        private readonly control: TmFormControl<T>,
        // not using constructor class field initialization here
        // to avoid naming parameter with an underscore
        isValid = true
    ) {
        this._isValid = isValid;
    }

    private _isValid = true;

    private readonly _errors: string[] = [];

    public addErrors(...message: string[]) {
        this._errors.push(...message);
        return this;
    }

    public get errors() {
        // return a copy to prevent external mutation
        return [...this._errors];
    }

    public get isValid() {
        if (this.errors.length > 0) {
            return false;
        }

        return this._isValid;
    }

    public get key() {
        return this.control.name;
    }

    public toString() {
        return this.errors.join(',');
    }

    /**
     * Combine this validation result with another, merging their error messages.
     *
     * @param other Another `FormControlValidationResult` instance from which to merge state.
     */
    public combine(other: FormControlValidationResult<T>) {
        const combined = new FormControlValidationResult(this.control);
        const combinedErrors = new Set([...this._errors, ...other.errors]);
        combined.addErrors(...Array.from(combinedErrors));

        return combined;
    }

    public compare(other: FormControlValidationResult<T>) {
        if (this.isValid !== other.isValid) {
            return false;
        }

        if (this._errors.length !== other.errors.length) {
            return false;
        }

        for (let i = 0; i < this._errors.length; i++) {
            if (this._errors[i] !== other.errors[i]) {
                return false;
            }
        }

        return true;
    }
}
